import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Lock, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* iter-81 — RestrictedPhone.
   Sibling to RestrictedEmail. For high-value ownership/executive
   contacts (Pedro Pereira, Dwight Tabales) the backend already scrubs
   `phone`/`phone_fmt` from the public response for non-admins — this
   component handles the UI side: rendering a blurred mask + an
   access-request button. Admins/engineers see the real phone via the
   regular call/WhatsApp row in PropertyDetailsSection because the
   backend leaves their payload untouched. */
export default function RestrictedPhone({ memberSlug, memberName }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState(false);

  const requestDisclosure = async () => {
    if (busy || requested) return;
    setBusy(true);
    try {
      if (!token) {
        // Anonymous visitors — hint at sign-in. Logged-in viewers/editors hit the API.
        toast.message('Sign in to request this contact phone — an admin will review your request.');
        return;
      }
      await axios.post(
        `${API}/access-requests`,
        {
          button: `contact-phone:${memberSlug}`,
          duration_hours: 24,
          reason: `Reveal phone for ${memberName || memberSlug}`,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRequested(true);
      toast.success(`Request sent — an admin will review your access to ${memberName || 'this contact'} shortly.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send request. Try again later.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 text-[12px]" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
      <button
        type="button"
        onClick={requestDisclosure}
        disabled={busy || requested}
        data-testid={`phone-restricted-${memberSlug}`}
        title={requested ? 'Request pending admin review' : 'Phone hidden — click to request disclosure'}
        className="flex-1 flex items-center gap-1.5 text-left disabled:cursor-default"
        style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" style={{ color: '#1F4FBE' }} />
        ) : (
          <Lock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: requested ? '#15803D' : '#B45309' }} />
        )}
        <span
          className="tabular-nums truncate transition-all"
          style={{ filter: requested ? 'none' : 'blur(5px)', userSelect: 'none' }}
          aria-hidden
        >
          {/* Plausible-length placeholder so the blur has something to obscure. */}
          +•• •••• ••••
        </span>
        <span
          className="text-[9px] font-bold uppercase tracking-wider flex-shrink-0 ml-1 px-1.5 py-0.5 rounded transition-colors"
          style={{
            color: requested ? '#15803D' : '#B45309',
            backgroundColor: requested ? '#DCFCE7' : '#FEF3C7',
          }}
        >
          {requested ? 'Requested' : 'Request'}
        </span>
      </button>
    </div>
  );
}
